import { getAuthHeaders, valtFetch, VALT_API_BASE } from '@/utils/api'

// API service for fetching user data from the real API endpoint
export interface ApiUser {
  id: number
  name: string
  email: string
  /** Used for SMS notifications, e.g. immobiliser schedules. */
  phone?: string | null
  role?: string | null
  admin: number | boolean
  admin_mode?: number | boolean
  manager: number | boolean
  manager_mode?: number | boolean
  operator: number | boolean
  operator_mode?: number | boolean
  driver: number | boolean
  driver_mode?: number | boolean
  owner?: number | boolean
  owner_mode?: number | boolean
  org_admin?: number | boolean
  org_admin_mode?: number | boolean
  team_manage?: number | boolean
  team_manage_mode?: number | boolean
  recruiter?: number | boolean
  recruiter_mode?: number | boolean
  finance?: number | boolean
  finance_mode?: number | boolean
  enabled: boolean | number
  api_key_enabled: number | boolean
  preferences: {
    units: {
      decimal: string | null
      distance: string
      money: string
      thousand: string | null
      volume: string | null
    }
  }
  language: {
    id: number
    name: string
    code: string
    locale: string
    rtl: number
  }
  timezone: {
    id: number
    zone: string
    default: boolean
  }
}

export interface UserRole {
  admin: boolean
  manager: boolean
  operator: boolean
  driver: boolean
}

export interface CreateUserData {
  name: string
  email: string
  password: string
  phone_number?: string
  role?: string | null
  admin?: number
  admin_mode?: number
  manager?: number
  manager_mode?: number
  operator?: number
  operator_mode?: number
  driver?: number
  driver_mode?: number
  owner?: number
  owner_mode?: number
  org_admin?: number
  org_admin_mode?: number
  team_manage?: number
  team_manage_mode?: number
  recruiter?: number
  recruiter_mode?: number
  finance?: number
  finance_mode?: number
  enabled?: boolean
  api_key_enabled?: number
  api_key?: string
  preferences?: {
    units: {
      decimal?: string
      distance?: string
      money?: string
      thousand?: string
      volume?: string
    }
  }
  language_id?: number
  timezone_id?: number
}

export interface UpdateUserData extends Partial<CreateUserData> {
  id: number
  /** Sent as-is; an empty string clears it. */
  phone?: string | null
}

interface ApiTimezone {
  id: number
  zone: string
  default?: boolean
}

interface ApiLanguage {
  id: number
  code: string
  name?: string
}

export class UserApiService {
  private static readonly API_BASE_URL = VALT_API_BASE

  private static async resolveDefaultTimezoneId(): Promise<number> {
    const response = await valtFetch(`${this.API_BASE_URL}/timezone`, {
      method: 'GET',
      mode: 'cors',
      headers: getAuthHeaders(),
    })
    if (!response.ok) {
      throw new Error(`Failed to load timezones: ${response.status} ${response.statusText}`)
    }
    const raw = await response.json()
    const items: ApiTimezone[] = Array.isArray(raw) ? raw : (raw?.data ?? [])
    const johannesburg = items.find((t) => t.zone === 'Africa/Johannesburg')
    if (johannesburg?.id) return johannesburg.id
    const markedDefault = items.find((t) => t.default)
    if (markedDefault?.id) return markedDefault.id
    if (items[0]?.id) return items[0].id
    throw new Error(
      'Timezone catalog is empty — seed the timezone table (php artisan db:seed --class=App\\Domains\\Timezone\\Seeder\\Timezone)',
    )
  }

  private static async resolveDefaultLanguageId(): Promise<number> {
    const response = await valtFetch(`${this.API_BASE_URL}/language`, {
      method: 'GET',
      mode: 'cors',
      headers: getAuthHeaders(),
    })
    if (!response.ok) {
      throw new Error(`Failed to load languages: ${response.status} ${response.statusText}`)
    }
    const raw = await response.json()
    const items: ApiLanguage[] = Array.isArray(raw) ? raw : (raw?.data ?? [])
    const english = items.find((l) => l.code === 'en')
    if (english?.id) return english.id
    if (items[0]?.id) return items[0].id
    throw new Error('Language catalog is empty — seed the language table')
  }

  // Fetch all users from the API
  static async getUsers(): Promise<ApiUser[]> {
    try {
      // Don't use region parameter for auth operations - use default DB
      const fullUrl = `${this.API_BASE_URL}/user`
      console.log('Fetching users from URL:', fullUrl)

      const response = await valtFetch(fullUrl, {
        method: 'GET',
        mode: 'cors',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`)
      }

      const users = await response.json()
      console.log('Fetched users from API:', users)
      return users
    } catch (error) {
      console.error('Error fetching users:', error)
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Get user by ID
  static async getUserById(id: number): Promise<ApiUser | null> {
    try {
      const users = await this.getUsers()
      return users.find((user) => user.id === id) || null
    } catch (error) {
      console.error('Error fetching user by ID:', error)
      throw error
    }
  }

  // Helper method to get region parameter from localStorage
  private static getRegionParam(): string {
    const region = localStorage.getItem('region')
    console.log('Region from localStorage:', region)

    if (region) {
      console.log('Using region parameter:', region)
      return `?region=${region}`
    }

    console.log('No region selected, using default API endpoint')
    return ''
  }

  // Update user
  static async updateUser(id: number, userData: UpdateUserData): Promise<ApiUser> {
    try {
      console.log(`Updating user ${id} with data:`, userData)
      console.log('Full payload being sent:', JSON.stringify(userData, null, 2))

      // Don't use region parameter for auth operations - use default DB
      const response = await valtFetch(`${this.API_BASE_URL}/user/${id}`, {
        method: 'PATCH',
        mode: 'cors',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to update user: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const updatedUser = await response.json()
      console.log('User updated successfully:', updatedUser)
      return updatedUser
    } catch (error) {
      console.error('Error updating user:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Create new user - follows POST /api/user/create API (see README_USER_REGISTRATION_ROLES_FRONTEND.md)
  // Don't use X-Region header - auth/user operations use default valt_os DB (consistent with getUsers, updateUser, deleteUser)
  static async createUser(userData: CreateUserData): Promise<ApiUser> {
    try {
      const payload: CreateUserData = { ...userData }

      // Timezone/language IDs differ per environment — always resolve against live catalogs
      const [timezoneId, languageId] = await Promise.all([
        this.resolveDefaultTimezoneId(),
        this.resolveDefaultLanguageId(),
      ])
      payload.timezone_id = timezoneId
      payload.language_id = languageId

      console.log('Creating user with data:', payload)
      console.log('Payload being sent:', JSON.stringify(payload, null, 2))

      const response = await valtFetch(`${this.API_BASE_URL}/user/create`, {
        method: 'POST',
        mode: 'cors',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        try {
          const errorData = JSON.parse(errorText)
          console.error('Parsed Error Data:', errorData)
          throw new Error(
            `Failed to create user: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`,
          )
        } catch (parseError) {
          throw new Error(`Failed to create user: ${response.status} ${response.statusText} - ${errorText}`)
        }
      }

      const result = await response.json()
      const createdUser = result.data ?? result
      console.log('User created successfully:', createdUser)
      return createdUser
    } catch (error) {
      console.error('Error creating user:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Delete user
  static async deleteUser(id: number): Promise<boolean> {
    try {
      console.log(`Deleting user ${id}`)

      // Don't use region parameter for auth operations - use default DB
      const response = await valtFetch(`${this.API_BASE_URL}/user/${id}`, {
        method: 'DELETE',
        mode: 'cors',
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to delete user: ${response.status} ${response.statusText} - ${errorText}`)
      }

      console.log(`User ${id} deleted successfully`)
      return true
    } catch (error) {
      console.error('Error deleting user:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the API. Please check your internet connection.')
      }
      throw error
    }
  }

  // Helper: API may return role flags as 1/0 or true/false
  private static isFlagSet(value: number | boolean | undefined | null): boolean {
    return value === 1 || value === true
  }

  // Helper function to determine user role from API flags (and optional role string)
  static getUserRole(user: ApiUser): string {
    const roleAttr = typeof user.role === 'string' ? user.role.trim().toLowerCase() : ''

    // Privileged flags win over a stale role string (API often leaves role as operator)
    if (this.isFlagSet(user.owner)) return 'owner'
    if (this.isFlagSet(user.org_admin)) return 'org_admin'
    if (this.isFlagSet(user.admin)) return 'admin'
    if (this.isFlagSet(user.manager)) return 'manager'

    if (roleAttr && roleAttr !== 'user' && roleAttr !== 'null') {
      if (roleAttr === 'support_agent') return 'operator'
      return roleAttr
    }

    // Flags — Laravel may cast tinyints to booleans; do not use === 1 only
    if (this.isFlagSet(user.team_manage)) return 'team_manager'
    if (this.isFlagSet(user.recruiter)) return 'recruiter'
    if (this.isFlagSet(user.finance)) return 'finance'
    if (this.isFlagSet(user.operator)) return 'operator'
    if (this.isFlagSet(user.driver)) return 'driver'
    return 'user'
  }

  // Helper function to get user permissions based on role
  static getUserPermissions(user: ApiUser): string[] {
    const role = this.getUserRole(user)
    const permissions: Record<string, string[]> = {
      admin: [
        'users.read',
        'users.write',
        'users.manage',
        'settings.read',
        'settings.write',
        'reports.read',
        'analytics.read',
      ],
      manager: ['users.read', 'users.write', 'reports.read', 'analytics.read'],
      operator: ['users.read', 'operations.read', 'operations.write'],
      driver: ['profile.read', 'profile.write', 'trips.read', 'trips.write'],
      team_manager: ['teams.read', 'teams.write', 'teams.manage', 'users.read'],
      owner: ['owner.read', 'owner.write', 'owner.manage', 'settings.read'],
      org_admin: ['org.read', 'org.write', 'org.manage', 'users.read'],
      recruiter: ['recruitment.read', 'recruitment.write', 'candidates.read'],
      finance: ['finance.read', 'finance.write', 'reports.read'],
      user: ['profile.read'],
    }
    return permissions[role] || permissions.user
  }

  // Helper function to get role color for UI
  static getRoleColor(role: string): string {
    const colorMap: Record<string, string> = {
      admin: 'danger',
      manager: 'warning',
      operator: 'primary',
      driver: 'info',
      team_manager: 'success',
      owner: 'danger',
      org_admin: 'warning',
      recruiter: 'info',
      finance: 'primary',
      user: 'secondary',
    }
    return colorMap[role] || 'secondary'
  }

  // Helper function to get role display name
  static getRoleDisplayName(role: string): string {
    const displayNames: Record<string, string> = {
      admin: 'Administrator',
      manager: 'Manager',
      operator: 'Operator',
      driver: 'Driver',
      team_manager: 'Team Manager',
      owner: 'Owner',
      org_admin: 'Organization Admin',
      recruiter: 'Recruiter',
      finance: 'Finance',
      user: 'User',
    }
    return displayNames[role] || 'User'
  }
}
